import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import LeadForm from 'components/forms/LeadForm';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useIndustries, resolveMandatoryFields, withAlwaysMandatoryField } from 'services/industryService';
import { hostnameFromUrl } from 'services/hostname';

// Style of the form on one page, strongest first: the page's own field overrides, the page's theme,
// the template's theme, the template's inline fields. Themes come as computed fields of
// GET /form-template/:id (themeStyle / pageThemeStyles) — absent when the seller uses none, so this
// is then exactly the template plus its page override, as before. Mirrors the backend's
// resolvePageStyle (services/formTheme.ts).
function resolveTemplateStyle(templateData: any, pageUrl?: string) {
    return {
        ...templateData,
        ...(templateData.themeStyle || {}),
        ...((pageUrl && templateData.pageThemeStyles?.[pageUrl]) || {}),
        ...((pageUrl && templateData.pageStyleOverrides?.[pageUrl]) || {}),
    };
}

// A universal iframe pasted identically on many pages has no per-page landingPage query param
// to key off — document.referrer is unreliable (browsers truncate it to origin-only for
// cross-origin loads by default). Instead, ask the Leadrs Pixel script running in the parent
// page's own context (which always knows its exact URL) over postMessage, and retry a few
// times in case the pixel's listener hasn't attached yet at the moment we first ask.
function requestParentPageUrl(timeoutMs = 1500): Promise<string | undefined> {
    return new Promise((resolve) => {
        if (window.parent === window) {
            resolve(undefined);
            return;
        }
        let settled = false;
        const handleMessage = (event: MessageEvent) => {
            if (event.data?.type === 'LEADRS_PAGE_URL' && typeof event.data.url === 'string') {
                settled = true;
                cleanup();
                resolve(event.data.url);
            }
        };
        const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            clearInterval(retryInterval);
        };
        window.addEventListener('message', handleMessage);
        const request = () => window.parent.postMessage({ type: 'LEADRS_REQUEST_PAGE_URL' }, '*');
        request();
        const retryInterval = setInterval(request, 300);
        setTimeout(() => {
            if (!settled) {
                cleanup();
                resolve(undefined);
            }
        }, timeoutMs);
    });
}

const PublicLeadForm = () => {
    const [searchParams] = useSearchParams();

    useEffect(() => {
        document.documentElement.style.background = 'transparent';
        document.body.style.background = 'transparent';
        return () => {
            document.documentElement.style.background = '';
            document.body.style.background = '';
        };
    }, []);
    const { industries: industriesData, loading: industriesLoading } = useIndustries();
    const [offer, setOffer] = useState<any>(null);
    const [template, setTemplate] = useState<any>(null);
    const [templateStyle, setTemplateStyle] = useState<any>(null);
    const [formBuilder, setFormBuilder] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isSideSellerMode, setIsSideSellerMode] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    // For universal templates with no `landingPage` query param, this holds the page URL
    // resolved via postMessage from the parent page's Pixel script (see requestParentPageUrl).
    // Falls back to the plain `landingPage` query param for every other case.
    const [resolvedLandingPage, setResolvedLandingPage] = useState<string | undefined>(undefined);
    // Best-effort URL of the page the iframe is embedded on, used for the RGPD consent record
    // and to name the host site in the consent sentence. `document.referrer` is available
    // immediately (origin-only for cross-origin, which is enough for the hostname); the Pixel
    // handshake below upgrades it to the full URL when the parent page runs our script.
    const [hostPageUrl, setHostPageUrl] = useState<string | undefined>(
        typeof document !== 'undefined' && document.referrer ? document.referrer : undefined
    );

    useEffect(() => {
        if (window.parent === window) return;
        requestParentPageUrl().then((url) => {
            if (url) setHostPageUrl(url);
        });
    }, []);

    const sellerId = searchParams.get('sellerId');
    const offerId = searchParams.get('offerId');
    const templateId = searchParams.get('templateId');
    const sideSellerId = searchParams.get('sideSellerId');
    const landingPage = searchParams.get('landingPage');
    const isChatbot = searchParams.get('chatbot') === '1';
    // Opened from the seller's dashboard (form preview): not a real page of their site, so it
    // must not count as a view nor register the dashboard as one of their pages.
    const isPreview = searchParams.get('preview') === '1';

    // Collect all extra query params as default field values (matched by label)
    const reservedParams = new Set(['sellerId', 'offerId', 'templateId', 'sideSellerId', 'landingPage', 'chatbot', 'preview']);
    const queryDefaults: Record<string, string> = {};
    searchParams.forEach((value, key) => {
        if (!reservedParams.has(key)) {
            queryDefaults[key] = value;
        }
    });

    useEffect(() => {
        const fetchOfferAndTemplate = async () => {
            try {
                setIsLoading(true);
                setError(null);
                // Baseline: the query param, as before. Overridden below only for a universal
                // template with no `landingPage` param (see requestParentPageUrl). Declared here
                // (function scope) so it's available for the view-tracking call after the
                // mode-specific branches below, not just inside Mode 1.
                let pageUrl: string | undefined = landingPage || undefined;
                setResolvedLandingPage(pageUrl);

                // Fetch formBuilder from API
                const formBuilderRes = await axios.get(`${CONFIG.apiUrl}/form-field`);
                // API returns { results: [...] }, but LeadForm expects { fields: [...] }
                setFormBuilder({ fields: formBuilderRes.data.results || [] });

                // Mode 1: Template-only (side-seller) mode
                if (templateId && !offerId) {
                    setIsSideSellerMode(true);
                    const templateRes = await axios.get(`${CONFIG.apiUrl}/form-template/${templateId}`);
                    const templateData = templateRes.data;
                    setTemplate(templateData);
                    setTemplateStyle(resolveTemplateStyle(templateData));

                    // Universal template: no fixed industry — resolve it dynamically from
                    // whatever the Pixel has already classified for this exact landing page.
                    // The page URL comes from the `landingPage` query param when the snippet was
                    // copied for a specific page; when the same snippet is pasted identically on
                    // many pages (no query param), ask the Pixel script in the parent page instead.
                    let resolvedIndustryName = templateData.industry;
                    let resolvedSubIndustryName = templateData.subIndustry;
                    if (templateData.isUniversal && !pageUrl) {
                        pageUrl = await requestParentPageUrl();
                    }
                    // No Pixel on the host page (missing, blocked, hidden Pixel mode): fall back to
                    // document.referrer — usually origin-only cross-origin, which is still enough for
                    // the domain-level activity, the view counter and the lead's landingPage. Without
                    // it the lead would reach the backend with no page at all ("Page non classée").
                    if (templateData.isUniversal && !pageUrl && window.parent !== window && document.referrer) {
                        pageUrl = document.referrer;
                    }
                    setResolvedLandingPage(pageUrl);
                    // Per-page design (theme and/or field overrides) — anything not customized for
                    // this exact page falls back to the template's theme, then its own style.
                    setTemplateStyle(resolveTemplateStyle(templateData, pageUrl));
                    // Resolution priority: the seller's activity for this exact page (an exception to
                    // its site's), then the activity of the whole site, then the template-wide default
                    // activity (site-generation MCP, any hostname, even with no page URL), then the raw
                    // Pixel classification. Mirrors the backend's resolveUniversalPageIndustry
                    // (services/universalPageIndustry.ts).
                    const domainOverride =
                        (pageUrl ? templateData.domainIndustryOverrides?.[hostnameFromUrl(pageUrl)] : undefined) ||
                        templateData.defaultIndustry;
                    const industryOverride = pageUrl ? templateData.pageIndustryOverrides?.[pageUrl] : undefined;
                    if (industryOverride?.validated && industryOverride.industry) {
                        resolvedIndustryName = industryOverride.industry;
                        resolvedSubIndustryName = industryOverride.subIndustry;
                    } else if (domainOverride?.industry) {
                        resolvedIndustryName = domainOverride.industry;
                        resolvedSubIndustryName = domainOverride.subIndustry;
                    } else if (templateData.isUniversal && pageUrl) {
                        try {
                            const industryRes = await axios.get(`${CONFIG.apiUrl}/leadrs-pixel/industry`, {
                                params: { url: pageUrl },
                            });
                            resolvedIndustryName = industryRes.data?.industry || undefined;
                            resolvedSubIndustryName = industryRes.data?.subIndustry || undefined;
                        } catch {
                            // Best-effort — fall through to the neutral fallback fields below.
                        }
                    }

                    // fieldOrder = seller's chosen order + optional field visibility
                    // mandatory fields always appended if not already in fieldOrder
                    // (sub-industry's mandatory fields override the industry's if set)
                    const industryData = industriesData.find(i => i.name === resolvedIndustryName);
                    const mandatoryFields: string[] = resolveMandatoryFields(industryData, resolvedSubIndustryName);
                    const fieldOrder: string[] = templateData.fieldOrder || [];
                    const merged = [...fieldOrder, ...mandatoryFields.filter((f: string) => !fieldOrder.includes(f))];
                    setOffer({ fields: merged.length ? merged : ['Prénom', 'Nom', 'Téléphone', 'Email', 'Code postal'] });
                }
                // Mode 2: Offer-based mode (with optional template styling)
                else if (sellerId && offerId) {
                    setIsSideSellerMode(false);

                    // Fetch offer details
                    const offerRes = await axios.get(
                        `${CONFIG.apiUrl}/offer/public/${offerId}`
                    );
                    const offerData = { ...offerRes.data };

                    // Always include industry (or sub-industry override) mandatory fields alongside offer fields
                    if (offerData.industry) {
                        const industryData = industriesData.find((i: any) => i.name === offerData.industry);
                        const mandatoryFields: string[] = resolveMandatoryFields(industryData, offerData.subIndustry);
                        const offerFields: string[] = offerData.fields || [];
                        offerData.fields = [...new Set([...offerFields, ...mandatoryFields.filter((f: string) => !offerFields.includes(f))])];
                    }

                    setOffer(offerData);

                    // Fetch template if templateId is present (for styling)
                    if (templateId) {
                        const templateRes = await axios.get(`${CONFIG.apiUrl}/form-template/${templateId}`);
                        setTemplateStyle(resolveTemplateStyle(templateRes.data));
                    }
                }
                // Missing required params
                else {
                    setError('Paramètres manquants: templateId seul OU (sellerId + offerId) sont requis');
                    setIsLoading(false);
                    return;
                }

                // Track page view — fire and forget, never blocks the form
                if (templateId && pageUrl && !isPreview) {
                    axios.post(`${CONFIG.apiUrl}/form-template/${templateId}/view`, { landingPage: pageUrl }).catch(() => {});
                }
            } catch (err: any) {
                console.error('Error fetching offer/template:', err);
                setError(
                    err.response?.data?.message || 
                    "Impossible de charger les informations de l'offre ou du template"
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchOfferAndTemplate();
    }, [sellerId, offerId, templateId, industriesData]);

    // Embedded form (not the chatbot panel): report its own height to the Pixel script running in
    // the parent page, which resizes this iframe to fit (pixelScript.ts) instead of the snippet's
    // fixed height="600". The outer wrapper is min-h-screen, so what's measured is the content
    // block plus that wrapper's vertical padding, never the viewport itself.
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (isChatbot || window.parent === window) return;
        const el = contentRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const report = () => {
            const wrapper = el.parentElement ? window.getComputedStyle(el.parentElement) : null;
            const padding = wrapper ? parseFloat(wrapper.paddingTop) + parseFloat(wrapper.paddingBottom) : 0;
            window.parent.postMessage({ type: 'LEADRS_FORM_HEIGHT', height: el.getBoundingClientRect().height + padding }, '*');
        };
        report();
        const observer = new ResizeObserver(report);
        observer.observe(el);
        return () => observer.disconnect();
    }, [isChatbot, isLoading, industriesLoading, isSubmitted, error]);

    // TrustedForm (ActiveProspect) certificate, loaded on this page only. Loaded once the form is
    // on screen so the script finds it: it adds a hidden xxTrustedFormCertUrl field that LeadForm
    // sends with the consent, and records the session (consent text and submit button are tagged
    // with data-tf-element-role in LeadForm).
    const trustedFormLoaded = useRef(false);
    const formShown = !isLoading && !industriesLoading && !error && !isSubmitted;
    useEffect(() => {
        if (!formShown || trustedFormLoaded.current) return;
        trustedFormLoaded.current = true;
        const tf = document.createElement('script');
        tf.async = true;
        tf.src = `https://api.trustedform.com/trustedform.js?field=xxTrustedFormCertUrl&use_tagged_consent=true&l=${Date.now()}${Math.random()}`;
        document.body.appendChild(tf);
    }, [formShown]);

    const handleSuccess = () => {
        setIsSubmitted(true);

        // Notify parent window if in iframe
        if (window.parent !== window) {
            window.parent.postMessage({ type: 'LEAD_FORM_SUCCESS' }, '*');
        }
    };

    const handleError = (error: any) => {
        console.error('Lead submission error:', error);
        // The backend returns internal/technical status strings (e.g. "Invalid lead",
        // "Empty submission") for logging and seller-facing tooling — never surface those
        // to the person filling out the public form. We branch on the HTTP status code
        // instead (stable, non-technical) to give a more specific, friendly French message.
        const statusCode = error.response?.status;
        let message: string;
        if (statusCode === 400) {
            message =
                "Votre formulaire semble vide ou incomplet. Merci de renseigner vos informations dans les champs ci-dessus avant d'envoyer votre demande.";
        } else if (statusCode === 403) {
            message =
                "Ce formulaire n'accepte plus de nouvelles demandes pour le moment. Merci de réessayer plus tard ou de contacter directement le site.";
        } else if (statusCode === 404) {
            message =
                "Une erreur est survenue avec le formulaire. Merci de vérifier que tous les champs sont correctement remplis, puis de réessayer.";
        } else {
            message =
                "Une erreur s'est produite lors de l'envoi de votre demande. Merci de vérifier votre connexion et de réessayer dans quelques instants.";
        }

        toast.error(message, {
            position: 'top-center',
            autoClose: 6000,
        });
    };

    // Brand colors for the chat header come from the template style once loaded; a sensible
    // default keeps the header from flashing gray while the seller's template is still fetching.
    const chatAccentColor = templateStyle?.buttonBackgroundColor || '#4f46e5';
    const chatAccentTextColor = templateStyle?.buttonTextColor || '#ffffff';

    const ChatHeader = () => (
        <div
            className="flex flex-shrink-0 items-center gap-3 px-4 py-3"
            style={{ backgroundColor: chatAccentColor, color: chatAccentTextColor }}
        >
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white/20">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12 2C6.48 2 2 5.94 2 10.8c0 2.68 1.41 5.08 3.6 6.68-.12.98-.5 2.34-1.35 3.68a.5.5 0 00.55.75c2.02-.45 3.6-1.28 4.55-1.9.99.3 2.05.47 3.15.47 5.52 0 10-3.94 10-8.8S17.52 2 12 2z"
                        fill="currentColor"
                    />
                </svg>
            </div>
            <div className="min-w-0">
                <p className="truncate text-sm font-semibold leading-tight">Assistant</p>
                <p className="truncate text-xs leading-tight opacity-80">
                    Répondez en 1 minute, on vous recontacte
                </p>
            </div>
        </div>
    );

    // In chatbot mode, every state (loading/error/form/success) renders inside this fixed-header
    // panel shell instead of the hero-page wrapper below — the header stays put while the body
    // beneath it scrolls, like a real chat widget.
    const chatShell = (children: React.ReactNode) => (
        <div className="flex h-screen flex-col overflow-hidden bg-white">
            <ChatHeader />
            <div className="flex-1 overflow-y-auto">{children}</div>
        </div>
    );

    if (isLoading || industriesLoading) {
        const spinner = (
            <div className={isChatbot ? 'flex h-full items-center justify-center p-6' : 'flex min-h-screen items-center justify-center bg-transparent'}>
                <div className="text-center">
                    <div className="mb-4 inline-block h-14 w-14 animate-spin rounded-full border-4 border-solid border-brand-500 border-r-transparent shadow-lg"></div>
                    <p className="text-base font-medium text-gray-700 animate-pulse">
                        Chargement du formulaire...
                    </p>
                </div>
            </div>
        );
        return isChatbot ? chatShell(spinner) : spinner;
    }

    if (error) {
        const errorContent = (
            <div className={isChatbot ? 'flex h-full items-center justify-center p-6' : 'flex min-h-screen items-center justify-center bg-transparent p-6'}>
                <div className="max-w-md text-center">
                    <div className="mb-4 text-6xl">⚠️</div>
                    <h2 className="mb-2 text-xl font-bold text-red-600 dark:text-red-400">
                        Erreur
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">{error}</p>
                </div>
            </div>
        );
        return isChatbot ? chatShell(errorContent) : errorContent;
    }

    if (!offer && !template) {
        const notFoundContent = (
            <div className={isChatbot ? 'flex h-full items-center justify-center p-6' : 'flex min-h-screen items-center justify-center bg-transparent p-6'}>
                <div className="max-w-md text-center">
                    <div className="mb-4 text-6xl">🔍</div>
                    <h2 className="mb-2 text-xl font-bold text-gray-700 dark:text-gray-300">
                        {isSideSellerMode ? 'Template introuvable' : 'Offre introuvable'}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        {isSideSellerMode
                            ? "Le template demandé n'existe pas ou n'est plus disponible."
                            : "L'offre demandée n'existe pas ou n'est plus disponible."
                        }
                    </p>
                </div>
            </div>
        );
        return isChatbot ? chatShell(notFoundContent) : notFoundContent;
    }

    if (isSubmitted) {
        const successContent = isChatbot ? (
            <div className="flex h-full items-center justify-center p-6 text-center animate-fade-in">
                <div>
                    <div className="mb-4 text-5xl">&#10003;</div>
                    <h2 className="mb-2 text-lg font-bold text-gray-800">Merci pour votre demande !</h2>
                    <p className="text-sm text-gray-600">
                        Votre formulaire a bien été envoyé. Nous reviendrons vers vous dans les plus brefs délais.
                    </p>
                </div>
                <style>{`
                    @keyframes fade-in {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in {
                        animation: fade-in 0.6s ease-out;
                    }
                `}</style>
            </div>
        ) : (
            <div className="min-h-screen bg-transparent p-4 md:p-8 lg:p-12 flex items-center justify-center">
                <div className="mx-auto max-w-lg text-center animate-fade-in">
                    <div className="relative">
                        <div className="relative rounded-3xl bg-white p-6 sm:p-10 md:p-14 shadow-2xl border border-gray-100">
                            <div className="mb-6 text-6xl">&#10003;</div>
                            <h2 className="mb-3 text-2xl md:text-3xl font-bold text-gray-800">
                                Merci pour votre demande !
                            </h2>
                            <p className="text-gray-600 text-base md:text-lg">
                                Votre formulaire a bien été envoyé. Nous reviendrons vers vous dans les plus brefs délais.
                            </p>
                        </div>
                    </div>
                </div>

                <style>{`
                    @keyframes fade-in {
                        from { opacity: 0; transform: translateY(-10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    .animate-fade-in {
                        animation: fade-in 0.6s ease-out;
                    }
                `}</style>
            </div>
        );
        return isChatbot ? chatShell(successContent) : successContent;
    }

    // L'email est présent dans toutes les iframes, même si le template/l'offre/l'industrie l'omet.
    const orderedOfferFields = withAlwaysMandatoryField(offer?.fields || []);

    // Host page for the RGPD consent record: the resolved landing page when we have it,
    // otherwise whatever we could learn about the embedding page.
    const consentPageUrl = resolvedLandingPage || landingPage || hostPageUrl || undefined;
    const consentHostName = hostnameFromUrl(consentPageUrl || '') || undefined;

    const toastContainer = (
        <ToastContainer
            position="top-center"
            autoClose={4000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
            toastClassName="!bg-white !shadow-2xl !rounded-2xl"
        />
    );

    const leadForm = (
        <LeadForm
            sellerId={sellerId || undefined}
            offerId={offerId || undefined}
            templateId={templateId || undefined}
            sideSellerId={sideSellerId || undefined}
            landingPage={resolvedLandingPage}
            isSideSellerMode={isSideSellerMode}
            offerFields={orderedOfferFields}
            formBuilder={formBuilder as any}
            templateStyle={templateStyle}
            queryDefaults={queryDefaults}
            hostName={consentHostName}
            pageUrl={consentPageUrl}
            compact={isChatbot}
            onSuccess={handleSuccess}
            onError={handleError}
        />
    );

    // Compact chat layout: no hero padding/max-width, no slide-up entrance (the panel itself
    // already animates open), footer trust line shrunk to fit under the fold instead of pushed
    // below it.
    if (isChatbot) {
        return chatShell(
            <div className="p-3">
                {toastContainer}
                {/* Bot message bubble — the header alone reads as a colored form title bar; this
                    is what actually sells "chat" over "form", like the first message a real
                    support widget shows before handing you an input. */}
                <div className="mb-4 flex items-end gap-2">
                    <div
                        className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundColor: chatAccentColor, color: chatAccentTextColor }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 2C6.48 2 2 5.94 2 10.8c0 2.68 1.41 5.08 3.6 6.68-.12.98-.5 2.34-1.35 3.68a.5.5 0 00.55.75c2.02-.45 3.6-1.28 4.55-1.9.99.3 2.05.47 3.15.47 5.52 0 10-3.94 10-8.8S17.52 2 12 2z"
                                fill="currentColor"
                            />
                        </svg>
                    </div>
                    <div className="max-w-[85%] rounded-2xl rounded-bl-sm bg-gray-100 px-3 py-2 text-sm text-gray-700">
                        👋 Bonjour ! Remplissez ce formulaire et nous vous recontactons rapidement.
                    </div>
                </div>
                {leadForm}
                <p className="mt-2 px-1 pb-2 text-center text-[11px] text-gray-400">
                    Vos données sont utilisées pour traiter votre demande et transmises uniquement avec votre consentement.
                </p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-transparent p-2 sm:p-4 md:p-8 lg:p-12">
            {toastContainer}

            <div ref={contentRef} className="mx-auto max-w-2xl">

                {/* Form Container */}
                <div className="relative animate-slide-up">
                    <div className="relative">
                        {leadForm}
                    </div>
                </div>

                {/* Footer Trust Elements */}
                <div className="mt-8 text-center space-y-3 animate-fade-in-delay-2">
                    <p className="text-xs text-gray-400">
                        Vos données sont utilisées pour traiter votre demande et transmises uniquement avec votre consentement.
                    </p>
                </div>
            </div>

            <style>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slide-up {
                    from { opacity: 0; transform: translateY(30px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fade-in 0.6s ease-out;
                }
                .animate-fade-in-delay {
                    animation: fade-in 0.6s ease-out 0.2s both;
                }
                .animate-fade-in-delay-2 {
                    animation: fade-in 0.6s ease-out 0.4s both;
                }
                .animate-slide-up {
                    animation: slide-up 0.7s ease-out 0.3s both;
                }
            `}</style>
        </div>
    );
};

export default PublicLeadForm;
